<template>
  <div
    v-if="rerender"
    :style="{
      background: value.sectionColor.backGround,
      color: value.sectionColor.text,
    }"
  >
    <v-card-title
      v-show="!!value.title"
      class="text-center justify-center pa-2"
    >
      {{ value.title }}
    </v-card-title>
    <v-card-subtitle v-show="!!value.description" class="ma-0 pa-2 text-center">
      {{ value.description }}
    </v-card-subtitle>

    <template v-for="(cat, i) in items">
      <v-card-subtitle class="pb-1 grey--text d-flex">
        {{ $t("special_sale") }}
        <v-spacer />
        <div
          :class="{ 'ended-timer': !cat.timer }"
          :style="{ color: !!cat.timer ? cat.color : '' }"
          class="d-flex align-center timer-wrapper"
        >
          <template v-if="!!cat.timer">
            <div :id="`sec.timer.${cat.id}`" />
            <div :id="`min.timer.${cat.id}`" />
            <div :id="`hrs.timer.${cat.id}`" />
            <div :id="`day.timer.${cat.id}`" />
            <v-icon :color="cat.color" left small>mdi-timer-sand</v-icon>
          </template>

          <span v-else class="red--text">{{ $t("ended") }}</span>
        </div>
      </v-card-subtitle>

      <div :key="i" class="d-flex mb-4 px-2">
        <v-card
          :color="cat.color"
          class="rounded-lg pa-2 d-flex flex-column align-center white--text my-2"
          flat
        >
          <v-icon color="white">{{
            cat.icon || "mdi-star-box-outline"
          }}</v-icon>
          <v-spacer />
          <span style="text-orientation: mixed; writing-mode: vertical-rl">{{
            cat.name
          }}</span>
          <v-spacer />
        </v-card>

        <swiper-wrapper
          :loop="false"
          :slides-per-view="2.5"
          :space-between="10"
          class="pa-2 flex ms-1"
          wrapper-class="special-slider"
        >
          <div
            v-for="(product, j) in cat.products"
            :key="j"
            class="swiper-slide"
            style="user-select: none"
          >
            <v-card class="rounded-lg overflow-hidden" outlined>
              <div
                v-if="hasDiscount(product)"
                :style="{ backgroundColor: cat.color }"
                class="discount-tag"
              >
                {{ use_lang(discountPercent(product)) }}٪
              </div>

              <v-img
                :alt="product.name"
                :src="product.gallery | getProductImage"
                aspect-ratio="1"
                contain
              />

              <v-card-title
                class="tree-line-ellipse py-0 my-2 text-break x-small-font"
                style="min-height: 45px"
                v-text="product.name"
              />

              <div class="pa-1 text-left">
                <span
                  v-if="hasDiscount(product)"
                  class="x-small-font grey--text text-decoration-line-through"
                >
                  {{ use_lang(price(product.price)) }} {{ $t("currency") }}
                </span>
                <br />
                <span class="small-font">
                  {{ use_lang(price(afterDiscount(product))) }}
                  {{ $t("currency") }}
                </span>
              </div>
            </v-card>
          </div>
        </swiper-wrapper>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SwiperWrapper from "@/view/pages/businessPage/components/cm/swiperWrapper.vue";

export default {
  name: "bSpecialCategoryView",
  components: { SwiperWrapper },

  props: {
    value: Object,
  },

  data() {
    return {
      items: [],
      timer: null,
      rerender: true,
    };
  },

  created() {
    this.$root.$on("fetchSpecialCategory", this.fetchSpecials);
    this.fetchSpecials();
  },
  watch: {
    "$i18n.locale": {
      handler() {
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
  },

  computed: {
    ...mapState("localstorage", ["createQrID"]),
  },

  methods: {
    fetchSpecials() {
      this.loading = true;

      this.$api
        .get("v1/testQr/category-special", {
          params: {
            qr_id: this.createQrID,
          },
        })
        .then(({ data }) => {
          // this.items = data.data
          this.items = [];
          this.rerender = false;

          this.$nextTick(() => {
            this.rerender = true;
            this.startCountdown(data.data);
          });
        })
        .finally(() => (this.loading = false));
    },
    hasDiscount(item) {
      return !!item.discount_amount || !!item.pivot?.total_discount;
    },
    afterDiscount(item) {
      if (!this.hasDiscount(item)) return item.price;

      let dp = Math.floor((item.price * this.discountPercent(item)) / 100);
      return item.price - dp;
    },
    discountPercent(item) {
      let specialDiscount = Number(item.pivot?.total_discount) || 0;

      if (!!specialDiscount) return specialDiscount;

      if (item.discount_type !== "percent") {
        return Math.floor((Number(item.discount_amount) * 100) / item.price);
      }

      return item.discount_amount;
    },
    startCountdown(items) {
      clearInterval(this.timer);
      this.timer = null;

      let now = Date.now();
      this.items = items.map((item) => {
        let time = new Date(item.expire_at).getTime() - now;
        return {
          ...item,
          timer: time > 0 ? time : false,
        };
      });

      this.$nextTick(() => {
        this.timer = setInterval(() => {
          let hasActiveTimers = false;
          this.items = this.items.map((item, index) => {
            if (item.timer) {
              const updatedTimer = this.handleCountdown(item.timer, item.id);
              if (updatedTimer) {
                hasActiveTimers = true;
                return { ...item, timer: updatedTimer };
              }
              return { ...item, timer: false };
            }
            return item;
          });

          if (!hasActiveTimers) {
            clearInterval(this.timer);
            this.timer = null;
          }
          this.$forceUpdate();
        }, 1000);
      });
    },
    handleCountdown(time, id) {
      let nextTime = time - 1000;
      if (nextTime <= 0) {
        // Timer has expired, return false to stop updating
        return false;
      }

      let ms = nextTime / 1000;

      // Get DOM elements
      let sec = document.getElementById(`sec.timer.${id}`);
      let min = document.getElementById(`min.timer.${id}`);
      let hrs = document.getElementById(`hrs.timer.${id}`);
      let day = document.getElementById(`day.timer.${id}`);

      // Only update if elements exist
      if (sec && min && hrs && day) {
        sec.innerText = Math.floor(ms % 60)
          .toString()
          .padStart(2, "0");
        min.innerText = Math.floor((ms / 60) % 60)
          .toString()
          .padStart(2, "0");
        hrs.innerText = Math.floor((ms / 60 / 60) % 24)
          .toString()
          .padStart(2, "0");

        let isDay =
          Math.floor((ms / 60 / 60 / 24 / 30) % 12) * 30 +
          Math.floor((ms / 60 / 60 / 24) % 30);
        if (isDay) {
          day.innerText = isDay.toString();
          day.classList.remove("d-none");
        } else {
          day.classList.add("d-none");
        }
      } else {
        // Log or handle missing elements (optional for debugging)
        console.warn(`Timer elements for id ${id} not found in DOM`);
      }

      return nextTime;
    },
  },

  beforeDestroy() {
    clearInterval(this.timer);
    this.timer = null;
  },
};
</script>

<style scoped>
.timer-wrapper {
  padding: 4px 8px;
  border: 1px solid rgba(119, 119, 119, 0.5);
  border-radius: 12px;
}

.timer-wrapper.ended-timer {
  border: 1px solid rgba(208, 40, 40, 0.5);
}

.timer-wrapper >>> div {
  position: relative;
  padding-inline: 4px;
  vertical-align: middle;
}

.timer-wrapper >>> div:not(:first-child):after {
  content: ":";
  position: absolute;
  right: 0;
  transform: translateX(2px);
}

.timer-wrapper >>> .v-icon {
  animation-name: rotate-icon;
  animation-duration: 3s;
  animation-iteration-count: infinite;
  -webkit-font-smoothing: antialiased;
  /*-webkit-font-smoothing: subpixel-antialiased;*/
  /*animation-direction: alternate;*/
  /*animation-fill-mode: both;*/
  /*animation-play-state: paused;*/
  transition: rotate 1s ease-out;
}

@keyframes rotate-icon {
  0% {
    transform: rotate(0deg);
  }
  45% {
    transform: rotate(180deg);
  }
  70% {
    transform: rotate(180deg);
  }
  75% {
    transform: rotate(100deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.discount-tag {
  position: absolute;
  top: 4px;
  left: 4px;
  border: 1px solid #fff;
  border-radius: 99px !important;
  padding: 2px 8px;
  font-size: x-small;
  color: #fff;
  line-height: normal;
}

.tree-line-ellipse {
  display: -webkit-box;
  -webkit-line-clamp: 3; /* Number of lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.small-font {
  font-size: small;
  line-height: normal;
}

.x-small-font {
  font-size: x-small;
  line-height: normal;
}
</style>
